// /app/frontend/src/pages/compliance/readiness/ReadinessListPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Filter, ChevronRight, Send, RefreshCw, Trash2,
  Building2, Clock, CheckCircle2, AlertTriangle, XCircle,
  FileText, Download, Ban
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../../components/ui/alert-dialog';

import { readinessAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500', icon: Send },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle2 },
  expired: { label: 'Expired', color: 'bg-red-500', icon: XCircle },
  voided: { label: 'Voided', color: 'bg-gray-500', icon: Ban }
};

const RATING_COLORS = {
  well_prepared: 'bg-green-500',
  progressing: 'bg-yellow-500',
  early_stage: 'bg-orange-500',
  significant_gaps: 'bg-red-500',
  not_started: 'bg-red-700'
};

export default function ReadinessListPage() {
  const navigate = useNavigate();
  const { isComplianceReadOnly: isReadOnly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteAssessment, setDeleteAssessment] = useState(null);
  const [voidId, setVoidId] = useState(null);
  const [sendId, setSendId] = useState(null);

  useEffect(() => {
    loadAssessments();
  }, [statusFilter, typeFilter]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.assessment_type = typeFilter;
      
      const response = await readinessAPI.getAll(params);
      setAssessments(response.data);
    } catch (error) {
      console.error('Error loading assessments:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!sendId) return;
    try {
      await readinessAPI.send(sendId);
      toast.success('Assessment sent successfully');
      setSendId(null);
      loadAssessments();
    } catch (error) {
      console.error('Error sending assessment:', error);
      toast.error('Failed to send assessment');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await readinessAPI.delete(deleteId);
      toast.success('Assessment deleted');
      setDeleteId(null);
      setDeleteAssessment(null);
      loadAssessments();
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment');
    }
  };

  const handleVoid = async () => {
    if (!voidId) return;
    try {
      await readinessAPI.void(voidId);
      toast.success('Assessment voided');
      setVoidId(null);
      loadAssessments();
    } catch (error) {
      console.error('Error voiding assessment:', error);
      toast.error('Failed to void assessment');
    }
  };

  const handleSendReminder = async (id) => {
    try {
      await readinessAPI.sendReminder(id);
      toast.success('Reminder sent');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    }
  };

  const filteredAssessments = assessments.filter(a => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      a.entity_name?.toLowerCase().includes(search) ||
      a.client_name?.toLowerCase().includes(search) ||
      a.recipient_name?.toLowerCase().includes(search) ||
      a.recipient_email?.toLowerCase().includes(search)
    );
  });

  // Stats
  const stats = {
    total: assessments.length,
    draft: assessments.filter(a => a.status === 'draft').length,
    sent: assessments.filter(a => a.status === 'sent').length,
    inProgress: assessments.filter(a => a.status === 'in_progress').length,
    completed: assessments.filter(a => a.status === 'completed').length
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="readiness-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Readiness Assessments</h1>
            <p className="text-muted-foreground">
              Baseline diagnostic assessments for prospective clients
            </p>
          </div>
          {!isReadOnly && (
            <Button onClick={() => navigate('/compliance/readiness/new')}>
              <Plus className="h-4 w-4 mr-2" />
              New Assessment
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-slate-500">{stats.draft}</div>
              <p className="text-sm text-muted-foreground">Draft</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-500">{stats.sent}</div>
              <p className="text-sm text-muted-foreground">Sent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-500">{stats.inProgress}</div>
              <p className="text-sm text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-500">{stats.completed}</div>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by entity, client, or recipient..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="voided">Voided</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="sme">SME</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={loadAssessments}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entity</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredAssessments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No assessments found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAssessments.map((assessment) => {
                  const statusConfig = STATUS_CONFIG[assessment.status] || STATUS_CONFIG.draft;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <TableRow 
                      key={assessment.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/compliance/readiness/${assessment.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{assessment.entity_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {assessment.client_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {assessment.assessment_type === 'enterprise' ? 'Enterprise' : 'SME'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{assessment.recipient_name}</div>
                          <div className="text-muted-foreground text-xs">{assessment.recipient_email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig.color} text-white`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {assessment.overall_score !== null ? (
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${RATING_COLORS[assessment.overall_rating] || 'bg-gray-400'}`} />
                            <span className="font-medium">{assessment.overall_score.toFixed(0)}%</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {assessment.completed_at 
                          ? new Date(assessment.completed_at).toLocaleDateString()
                          : assessment.sent_at
                          ? new Date(assessment.sent_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/compliance/readiness/${assessment.id}`);
                            }}>
                              View Details
                            </DropdownMenuItem>
                            {!isReadOnly && assessment.status === 'draft' && (
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setSendId(assessment.id);
                              }}>
                                <Send className="h-4 w-4 mr-2" />
                                Send Assessment
                              </DropdownMenuItem>
                            )}
                            {['sent', 'in_progress'].includes(assessment.status) && (
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleSendReminder(assessment.id);
                              }}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Send Reminder
                              </DropdownMenuItem>
                            )}
                            {!isReadOnly && ['draft', 'sent', 'in_progress', 'expired'].includes(assessment.status) && (
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(assessment.id);
                                  setDeleteAssessment(assessment);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                            {!isReadOnly && assessment.status === 'completed' && (
                              <DropdownMenuItem
                                className="text-muted-foreground"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVoidId(assessment.id);
                                }}
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Void
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Send Dialog */}
        <AlertDialog open={!!sendId} onOpenChange={() => setSendId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send Assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will send an email invitation to the recipient. They will have access to complete the assessment.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleSend}>Send</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => { setDeleteId(null); setDeleteAssessment(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteAssessment?.status === 'in_progress'
                  ? 'The respondent has started this assessment. Deleting will discard their progress. This action cannot be undone.'
                  : 'This action cannot be undone. The assessment will be permanently deleted.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Void Dialog */}
        <AlertDialog open={!!voidId} onOpenChange={() => setVoidId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Void Assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark the assessment as voided and hide it from active views. The record will be kept for audit purposes. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleVoid}>
                Void Assessment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
